import { useState, useEffect, useCallback } from 'react';
import { apiUrl, apiFetch, getHeaders, getImageDisplayUrl } from '../../../config/api';
import {
    fetchPlaces as fetchPlacesService,
    fetchPlaceLedger as fetchPlaceLedgerService,
    addPlaceLedgerBalance as addPlaceLedgerBalanceService,
    fetchCategories as fetchCategoriesService,
    fetchTags as fetchTagsService,
    fetchBanners as fetchBannersService,
    fetchFeaturedItems as fetchFeaturedItemsService,
    fetchUsers as fetchUsersService,
    fetchBusinessSubmissions as fetchBusinessSubmissionsService,
    fetchAnalytics as fetchAnalyticsService,
    fetchTopicsAdmin as fetchTopicsAdminService,
    fetchRedemptions as fetchRedemptionsService,
    fetchRedemptionDeletionHistory as fetchRedemptionDeletionHistoryService,
    deleteRedemption as deleteRedemptionService,
    fetchPlaceRedemptionStats as fetchPlaceRedemptionStatsService,
    fetchAmbassadors as fetchAmbassadorsService,
    createAmbassador as createAmbassadorService,
    updateAmbassador as updateAmbassadorService,
    deleteAmbassadorWithReason as deleteAmbassadorWithReasonService,
    fetchAmbassadorRedemptions as fetchAmbassadorRedemptionsService,
    fetchAmbassadorDeletionHistory as fetchAmbassadorDeletionHistoryService,
    fetchReports as fetchReportsService,
    updateReportStatus as updateReportStatusService,
} from '../../../api/services/admin';
import { INITIAL_CATEGORY_FORM, INITIAL_TAG_FORM, INITIAL_BANNER_FORM, INITIAL_FEATURED_ITEM_FORM, INITIAL_PLACE_FORM } from '../constants';
import { toIsoUtcMidnight, toDateKey, todayDateKey } from '../../../utils/dates';

const DEFAULT_OPENING_HOURS = {
    monday: { open: '', close: '' },
    tuesday: { open: '', close: '' },
    wednesday: { open: '', close: '' },
    thursday: { open: '', close: '' },
    friday: { open: '', close: '' },
    saturday: { open: '', close: '' },
    sunday: { open: '', close: '' },
};

export function useAdminDashboard(token) {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [stats, setStats] = useState({});
    const [places, setPlaces] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [banners, setBanners] = useState([]);
    const [featuredItems, setFeaturedItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [businessSubmissions, setBusinessSubmissions] = useState([]);
    const [analytics, setAnalytics] = useState({});
    const [selectedBusinessSubmission, setSelectedBusinessSubmission] = useState(null);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [businessSubmissionDetailsOpen, setBusinessSubmissionDetailsOpen] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [mainImageIndex, setMainImageIndex] = useState(0);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [menuImages, setMenuImages] = useState([]);
    const [uploadingMenuImages, setUploadingMenuImages] = useState(false);
    const [uploadingBannerImage, setUploadingBannerImage] = useState(false);
    const [uploadingFeaturedItemImage, setUploadingFeaturedItemImage] = useState(false);
    const [placeDialog, setPlaceDialog] = useState(false);
    const [placeDetailsOpen, setPlaceDetailsOpen] = useState(false);
    const [bannerDialog, setBannerDialog] = useState(false);
    const [featuredItemDialog, setFeaturedItemDialog] = useState(false);
    const [categoryDialog, setCategoryDialog] = useState(false);
    const [placeForm, setPlaceForm] = useState({ ...INITIAL_PLACE_FORM, openingHours: { ...DEFAULT_OPENING_HOURS } });
    const [categoryForm, setCategoryForm] = useState({ ...INITIAL_CATEGORY_FORM });
    const [tagDialog, setTagDialog] = useState(false);
    const [tagForm, setTagForm] = useState({ ...INITIAL_TAG_FORM });
    const [bannerForm, setBannerForm] = useState({ ...INITIAL_BANNER_FORM });
    const [featuredItemForm, setFeaturedItemForm] = useState({ ...INITIAL_FEATURED_ITEM_FORM });
    const [topics, setTopics] = useState([]);
    const [redemptions, setRedemptions] = useState([]);
    const [redemptionsTotal, setRedemptionsTotal] = useState(0);
    const [redemptionDeletionHistory, setRedemptionDeletionHistory] = useState([]);
    const [redemptionFilters, setRedemptionFilters] = useState({ placeId: '', userId: '', offerId: '', ambassadorCode: '', dateFrom: '', dateTo: '', search: '', limit: 50, offset: 0 });
    const [selectedPlaceRedemptionStats, setSelectedPlaceRedemptionStats] = useState(null);
    const [ambassadors, setAmbassadors] = useState([]);
    const [ambassadorFilters, setAmbassadorFilters] = useState({ search: '', code: '', dateFrom: '', dateTo: '', minRedeems: '', maxRedeems: '' });
    const [ambassadorRedemptions, setAmbassadorRedemptions] = useState([]);
    const [ambassadorDeletionHistory, setAmbassadorDeletionHistory] = useState([]);
    /** RedemptionDeletionAuditLog rows for the ambassador drill-down modal (attributed redemption deletes). */
    const [ambassadorRedemptionDeletionAudit, setAmbassadorRedemptionDeletionAudit] = useState([]);
    const [reports, setReports] = useState([]);
    const [reportsSummary, setReportsSummary] = useState({ total: 0, open: 0 });
    const [reportFilters, setReportFilters] = useState({ status: '' });

    const fetchPlaces = useCallback(async (headers) => {
        try {
            const data = await fetchPlacesService(headers);
            if (data != null) setPlaces(data);
        } catch (e) { console.error('Failed to fetch places:', e); }
    }, []);
    const fetchCategories = useCallback(async (headers) => {
        try {
            const data = await fetchCategoriesService(headers);
            if (data != null) setCategories(data);
        } catch (e) { console.error('Failed to fetch categories:', e); }
    }, []);
    const fetchTags = useCallback(async (headers) => {
        try {
            const data = await fetchTagsService(headers);
            if (data != null) setTags(data);
        } catch (e) { console.error('Failed to fetch tags:', e); }
    }, []);
    const fetchBanners = useCallback(async (headers) => {
        try {
            const data = await fetchBannersService(headers);
            if (data != null) setBanners(data);
        } catch (e) { console.error('Failed to fetch banners:', e); }
    }, []);
    const fetchFeaturedItems = useCallback(async (headers) => {
        try {
            const data = await fetchFeaturedItemsService(headers);
            if (data != null) setFeaturedItems(data);
        } catch (e) { console.error('Failed to fetch featured items:', e); }
    }, []);
    const fetchUsers = useCallback(async (headers) => {
        try {
            const data = await fetchUsersService(headers);
            if (data != null) setUsers(data);
        } catch (e) { console.error('Failed to fetch users:', e); }
    }, []);
    const fetchBusinessSubmissions = useCallback(async (headers) => {
        try {
            const data = await fetchBusinessSubmissionsService(headers);
            if (data != null) setBusinessSubmissions(data);
        } catch (e) { console.error('Failed to fetch business submissions:', e); }
    }, []);
    const fetchAnalytics = useCallback(async (headers) => {
        try {
            const result = await fetchAnalyticsService(headers);
            if (result != null) {
                setStats(result.stats);
                setAnalytics(result.analytics);
            }
        } catch (e) { console.error('Failed to fetch analytics:', e); }
    }, []);
    const fetchTopics = useCallback(async (headers) => {
        try {
            const data = await fetchTopicsAdminService(headers);
            if (data != null) setTopics(data);
        } catch (e) { console.error('Failed to fetch topics:', e); }
    }, []);
    const fetchRedemptions = useCallback(async (headers, filters = redemptionFilters) => {
        try {
            const data = await fetchRedemptionsService(headers, filters);
            if (data != null) {
                setRedemptions(data.items || []);
                setRedemptionsTotal(data.total || 0);
            }
        } catch (e) { console.error('Failed to fetch redemptions:', e); }
    }, [redemptionFilters]);
    const fetchRedemptionDeletionHistory = useCallback(async (headers) => {
        try {
            const data = await fetchRedemptionDeletionHistoryService(headers);
            if (data != null) setRedemptionDeletionHistory(data);
        } catch (e) { console.error('Failed to fetch deletion history:', e); }
    }, []);
    const fetchAmbassadors = useCallback(async (headers, filters = ambassadorFilters) => {
        try {
            const data = await fetchAmbassadorsService(headers, filters);
            if (data != null) setAmbassadors(data);
        } catch (e) { console.error('Failed to fetch ambassadors:', e); }
    }, [ambassadorFilters]);
    const fetchAmbassadorDeletionHistory = useCallback(async (headers) => {
        try {
            const data = await fetchAmbassadorDeletionHistoryService(headers);
            if (data != null) setAmbassadorDeletionHistory(data);
        } catch (e) { console.error('Failed to fetch ambassador deletion history:', e); }
    }, []);
    const fetchReports = useCallback(async (headers, params = reportFilters) => {
        try {
            const data = await fetchReportsService(headers, params);
            if (data != null) {
                setReports(data.items || []);
                setReportsSummary({ total: data.total || 0, open: data.open || 0 });
            }
        } catch (e) { console.error('Failed to fetch reports:', e); }
    }, [reportFilters]);

    const handleApplyReportFilters = useCallback(async (nextFilters) => {
        const next = nextFilters || { status: '' };
        setReportFilters(next);
        await fetchReports(getHeaders(token), next);
    }, [token, fetchReports]);

    const handleUpdateReportStatus = useCallback(async (reportId, status) => {
        try {
            const data = await updateReportStatusService(getHeaders(token), reportId, status);
            if (data && data.success) {
                setSuccess('Report updated');
                await fetchReports(getHeaders(token));
            } else {
                setError(data?.error?.message || 'Failed to update report');
            }
        } catch (e) { setError('Failed to update report: ' + e.message); }
    }, [token, fetchReports]);

    const loadDashboardData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const headers = getHeaders(token);
            switch (tabValue) {
                case 0:
                    await Promise.all([fetchAnalytics(headers), fetchTags(headers)]);
                    break;
                case 1:
                    await Promise.all([fetchPlaces(headers), fetchTags(headers), fetchCategories(headers)]);
                    break;
                case 2:
                    await fetchCategories(headers);
                    break;
                case 3:
                    await fetchTags(headers);
                    break;
                case 4:
                    await Promise.all([fetchBanners(headers), fetchCategories(headers)]);
                    break;
                case 8:
                    await Promise.all([fetchTopics(headers), fetchPlaces(headers)]);
                    break;
                case 9:
                    await Promise.all([fetchRedemptions(headers), fetchRedemptionDeletionHistory(headers)]);
                    break;
                case 10:
                    await Promise.all([fetchAmbassadors(headers), fetchAmbassadorDeletionHistory(headers)]);
                    break;
                case 5:
                    await fetchUsers(headers);
                    break;
                case 6:
                    await fetchBusinessSubmissions(headers);
                    break;
                case 7:
                    await fetchAnalytics(headers);
                    break;
                case 12:
                    await fetchReports(headers);
                    break;
                default:
                    break;
            }
        } catch (e) {
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [tabValue, token, fetchPlaces, fetchCategories, fetchTags, fetchBanners, fetchUsers, fetchBusinessSubmissions, fetchAnalytics, fetchTopics, fetchRedemptions, fetchRedemptionDeletionHistory, fetchAmbassadors, fetchAmbassadorDeletionHistory, fetchReports]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    const handleUpdateBusinessStatus = useCallback(async (submissionId, newStatus) => {
        const statusToSend = (newStatus === 'pending' || newStatus === 'approved' || newStatus === 'rejected') ? "didn't_contact_yet" : newStatus;
        try {
            const res = await apiFetch(apiUrl(`/contact/business/${submissionId}/status`), { method: 'PUT', headers: getHeaders(token), body: JSON.stringify({ status: statusToSend }) });
            const data = await res.json();
            if (res.ok) { setSuccess('Business submission status updated successfully!'); loadDashboardData(); }
            else setError(data.error?.message || 'Failed to update business submission status');
        } catch (e) { setError('Failed to update business submission status: ' + e.message); }
    }, [token, loadDashboardData]);

    const handleImageUpload = useCallback(async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;
        setUploadingImages(true);
        try {
            const formData = new FormData();
            files.forEach(f => formData.append('images', f));
            const headers = getHeaders(token);
            delete headers['Content-Type'];
            const res = await apiFetch(apiUrl('/places/upload-images'), { method: 'POST', headers, body: formData });
            const data = await res.json();
            if (res.ok) {
                const urls = (data.data?.urls || []).filter(u => typeof u === 'string' && u.trim()).map(u => getImageDisplayUrl(u));
                setUploadedImages(prev => [...prev, ...urls]);
                setSuccess('Images uploaded successfully');
            } else setError(data.error?.message || 'Failed to upload images');
        } catch (e) { setError('Failed to upload images'); }
        finally { setUploadingImages(false); }
    }, [token]);

    const handleRemoveImage = useCallback((index) => {
        const newImages = uploadedImages.filter((_, i) => i !== index);
        setUploadedImages(newImages);
        if (mainImageIndex >= newImages.length) setMainImageIndex(Math.max(0, newImages.length - 1));
    }, [uploadedImages, mainImageIndex]);

    const handleMenuImageUpload = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
        setUploadingMenuImages(true);
        try {
            const formData = new FormData();
            files.forEach(f => formData.append('images', f));
            const headers = getHeaders(token);
            delete headers['Content-Type'];
            const res = await apiFetch(apiUrl('/places/upload-images'), { method: 'POST', headers, body: formData });
            const data = await res.json();
            if (res.ok) {
                const urls = (data.data?.urls || []).filter(u => typeof u === 'string' && u.trim()).map(u => getImageDisplayUrl(u));
                setMenuImages(prev => [...prev, ...urls]);
                setSuccess('Menu photos uploaded successfully');
            } else setError(data.error?.message || 'Failed to upload menu photos');
        } catch (e) { setError('Failed to upload menu photos'); }
        finally { setUploadingMenuImages(false); if (event.target) event.target.value = ''; }
    }, [token]);

    const handleRemoveMenuImage = useCallback((index) => {
        setMenuImages(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleBannerImageUpload = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploadingBannerImage(true);
        try {
            const formData = new FormData();
            formData.append('images', file);
            const headers = getHeaders(token);
            delete headers['Content-Type'];
            const res = await apiFetch(apiUrl('/places/upload-images'), { method: 'POST', headers, body: formData });
            const data = await res.json();
            if (res.ok && data.data?.urls?.length) {
                const url = getImageDisplayUrl(data.data.urls[0]);
                if (url) setBannerForm(prev => ({ ...prev, imageUrl: url }));
                setSuccess('Image uploaded');
            } else setError(data.error?.message || 'Failed to upload image');
        } catch (e) { setError('Failed to upload image'); }
        finally { setUploadingBannerImage(false); event.target.value = ''; }
    }, [token]);

    const handleFeaturedItemImageUpload = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploadingFeaturedItemImage(true);
        try {
            const formData = new FormData();
            formData.append('images', file);
            const headers = getHeaders(token);
            delete headers['Content-Type'];
            const res = await apiFetch(apiUrl('/places/upload-images'), { method: 'POST', headers, body: formData });
            const data = await res.json();
            if (res.ok && data.data?.urls?.length) {
                const url = getImageDisplayUrl(data.data.urls[0]);
                if (url) setFeaturedItemForm(prev => ({ ...prev, imageUrl: url }));
                setSuccess('Image uploaded');
            } else setError(data.error?.message || 'Failed to upload image');
        } catch (e) { setError('Failed to upload image'); }
        finally { setUploadingFeaturedItemImage(false); event.target.value = ''; }
    }, [token]);

    const handleSetMainImage = useCallback((index) => setMainImageIndex(index), []);

    const handleCreatePlace = useCallback(async () => {
        try {
            const isEditing = placeForm._id;
            const addr = (placeForm.address || placeForm.location?.address || '').trim();
            const mapsLink = (placeForm.googleMapsLink || placeForm.location?.googleMapsLink || '').trim();
            if (!addr) { setError('Address is required'); return; }
            if (!mapsLink) { setError('Google Maps link is required'); return; }
            if (uploadedImages.length === 0) { setError('Please add at least one image.'); return; }
            const formData = {
                name: placeForm.name,
                description: placeForm.description,
                category: placeForm.category || placeForm.categories?.[0] || '',
                priceRange: placeForm.priceRange || 'mid',
                location: { address: String(addr), googleMapsLink: String(mapsLink) },
                tags: placeForm.vibeTags || placeForm.tags || [],
                famousTags: Array.isArray(placeForm.famousTags) ? placeForm.famousTags : (placeForm.famousTags ? [placeForm.famousTags] : []),
                primaryTag: (placeForm.primaryTag || '').trim(),
                openingHours: placeForm.openingHours || {},
                comment: placeForm.comment || '',
                contactInfo: { phoneNumber: (placeForm.contactInfo?.phoneNumber || '').trim(), ...(placeForm.contactInfo?.email?.trim() ? { email: placeForm.contactInfo.email.trim() } : {}) },
                images: uploadedImages,
                mainImageIndex: mainImageIndex || 0,
                menuImages,
                offers: (placeForm.offers || []).map((o) => ({
                    id: o.id || `offer-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    name: (o.name || '').trim(),
                    description: (o.description || '').trim(),
                    commissionPercent: o.commissionPercent != null && o.commissionPercent !== '' ? Number(o.commissionPercent) : '',
                    cashbackPercent: o.cashbackPercent != null && o.cashbackPercent !== '' ? Number(o.cashbackPercent) : '',
                    code: (o.code || '').trim() || undefined,
                    maxRedemptions: o.maxRedemptions != null && o.maxRedemptions !== '' ? parseInt(o.maxRedemptions, 10) : undefined,
                    startDate: toIsoUtcMidnight(o.startDate),
                    expiryDate: toIsoUtcMidnight(o.expiryDate),
                })).filter((o) => o.name && o.description && o.startDate && o.expiryDate),
            };
            if (isEditing && (placeForm.contributorId || '').trim()) {
                formData.contributorId = String(placeForm.contributorId).trim();
            }
            if (placeForm.ledgerMinLe !== '' && placeForm.ledgerMinLe != null) {
                formData.ledgerMinLe = Number(placeForm.ledgerMinLe);
            }
            // Always send these so clearing a field resets it (blank = 0 / none / global default).
            formData.commissionPercent = placeForm.commissionPercent !== '' && placeForm.commissionPercent != null ? Number(placeForm.commissionPercent) : 0;
            formData.cashbackPercent = placeForm.cashbackPercent !== '' && placeForm.cashbackPercent != null ? Number(placeForm.cashbackPercent) : '';
            formData.pointsCapPercent = placeForm.pointsCapPercent !== '' && placeForm.pointsCapPercent != null ? Number(placeForm.pointsCapPercent) : '';
            formData.marketingFeeLe = placeForm.marketingFeeLe !== '' && placeForm.marketingFeeLe != null ? Number(placeForm.marketingFeeLe) : '';
            formData.marketingFeeMinBillLe = placeForm.marketingFeeMinBillLe !== '' && placeForm.marketingFeeMinBillLe != null ? Number(placeForm.marketingFeeMinBillLe) : '';
            formData.marketingFeeMaxLe = placeForm.marketingFeeMaxLe !== '' && placeForm.marketingFeeMaxLe != null ? Number(placeForm.marketingFeeMaxLe) : '';
            const url = isEditing ? apiUrl(`/places/${placeForm._id}`) : apiUrl('/places');
            const res = await apiFetch(url, { method: isEditing ? 'PUT' : 'POST', headers: getHeaders(token), body: JSON.stringify(formData) });
            const data = await res.json();
            if (res.ok) {
                if (uploadedImages.length > 0) {
                    const placeId = isEditing ? placeForm._id : data.data.place._id;
                    const imgRes = await apiFetch(apiUrl(`/places/${placeId}/images`), { method: 'PUT', headers: getHeaders(token), body: JSON.stringify({ images: uploadedImages, mainImageIndex }) });
                    if (!imgRes.ok) setError('Place created but failed to save images');
                }
                setSuccess(`Place ${isEditing ? 'updated' : 'created'} successfully!`);
                setPlaceDialog(false);
                setPlaceForm({ ...INITIAL_PLACE_FORM, openingHours: { ...DEFAULT_OPENING_HOURS } });
                setUploadedImages([]);
                setMainImageIndex(0);
                setMenuImages([]);
                loadDashboardData();
            } else {
                const errDetails = data.error?.details || [];
                const errMsg = Array.isArray(errDetails) && errDetails.length > 0
                    ? errDetails.map(err => (typeof err === 'object' ? Object.entries(err).map(([k, v]) => `${k}: ${v}`).join(', ') : String(err))).join('; ')
                    : data.error?.message || `Failed to ${isEditing ? 'update' : 'create'} place`;
                setError(errMsg);
            }
        } catch (e) { setError(`Failed to ${placeForm._id ? 'update' : 'create'} place: ` + e.message); }
    }, [token, placeForm, uploadedImages, mainImageIndex, menuImages, loadDashboardData]);

    const handleCreateBanner = useCallback(async () => {
        try {
            const isEditing = bannerForm._id;
            const featureType = bannerForm.featureType || bannerForm.targetType || 'place';
            const rawTargetId = bannerForm.targetId;
            const targetId = typeof rawTargetId === 'string' ? rawTargetId.trim() : String(rawTargetId || '').trim();

            if (!featureType) {
                setError('Please select a banner target type.');
                return;
            }
            if (!targetId) {
                setError('Please select a target item for this banner.');
                return;
            }
            if (featureType === 'place' && !/^[a-fA-F0-9]{24}$/.test(targetId)) {
                setError('Invalid place target. Please re-select a valid place.');
                return;
            }

            let startDateISO = toIsoUtcMidnight(bannerForm.startDate) || new Date().toISOString();
            let endDateISO = null;
            if (bannerForm.endDate?.trim()) {
                endDateISO = toIsoUtcMidnight(bannerForm.endDate);
            }
            const payload = {
                title: bannerForm.title.trim(),
                description: bannerForm.description?.trim() || '',
                imageUrl: bannerForm.imageUrl.trim(),
                featureType,
                targetId,
                isFeaturedPlace: featureType === 'place',
                order: parseInt(bannerForm.order) || 0,
                isActive: bannerForm.isActive !== false,
                startDate: startDateISO,
                ...(endDateISO && { endDate: endDateISO }),
            };
            const url = isEditing ? apiUrl(`/features/${bannerForm._id}`) : apiUrl('/features');
            const res = await apiFetch(url, { method: isEditing ? 'PUT' : 'POST', headers: getHeaders(token), body: JSON.stringify(payload) });
            const data = await res.json();
            if (res.ok) {
                setSuccess(`Banner ${isEditing ? 'updated' : 'created'} successfully!`);
                setBannerDialog(false);
                setBannerForm({ ...INITIAL_BANNER_FORM });
                loadDashboardData();
            } else {
                const details = data?.error?.details;
                const detailsMsg = details && typeof details === 'object'
                    ? Object.entries(details)
                        .map(([field, entry]) => `${field}: ${entry?.msg || entry?.message || String(entry)}`)
                        .join('; ')
                    : '';
                setError(detailsMsg || data.error?.message || `Failed to ${isEditing ? 'update' : 'create'} banner`);
            }
        } catch (e) { setError(`Failed to ${bannerForm._id ? 'update' : 'create'} banner: ` + e.message); }
    }, [token, bannerForm, loadDashboardData]);

    const handleViewPlace = useCallback(async (place) => {
        if (!place?._id) return;
        try {
            const res = await apiFetch(apiUrl(`/places/${place._id}`), { headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok && data.data?.place) setSelectedPlace(data.data.place);
            else setSelectedPlace(place);
        } catch (e) {
            setSelectedPlace(place);
        }
        setPlaceDetailsOpen(true);
    }, [token]);

    const contributorIdFromPlace = (place) => {
        const c = place?.contributorId;
        if (c == null || c === '') return '';
        if (typeof c === 'string') return c.trim();
        if (typeof c === 'object' && c._id != null) return String(c._id).trim();
        if (typeof c === 'object' && typeof c.toString === 'function') {
            const s = String(c.toString()).trim();
            if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
        }
        const s = String(c).trim();
        return /^[a-fA-F0-9]{24}$/.test(s) ? s : '';
    };

    const placeImageUrlsForForm = (place) => {
        const imgs = place?.images;
        if (!Array.isArray(imgs)) return [];
        return imgs
            .map((img) => {
                if (typeof img === 'string') return img.trim();
                if (img && typeof img === 'object') {
                    const u = img.url || img.path || img.src || '';
                    return typeof u === 'string' ? u.trim() : '';
                }
                return '';
            })
            .map((u) => getImageDisplayUrl(u))
            .filter(Boolean);
    };

    const handleEditPlace = useCallback(async (place) => {
        const headers = getHeaders(token);
        await fetchCategories(headers);
        const ledger = await fetchPlaceLedgerService(headers, place._id);
        const loc = place.location || {};
        const contributorIdStr = contributorIdFromPlace(place);
        setPlaceForm({
            _id: place._id,
            name: place.name || '',
            contributorId: contributorIdStr,
            description: place.description || '',
            category: place.category || place.categories?.[0] || '',
            priceRange: place.priceRange || 'mid',
            vibeTags: place.tags || place.vibeTags || [],
            tags: place.tags || [],
            address: loc.address || place.address || '',
            googleMapsLink: loc.googleMapsLink || place.googleMapsLink || '',
            location: loc,
            contactInfo: place.contactInfo || { phoneNumber: '', email: '' },
            openingHours: place.openingHours || { ...DEFAULT_OPENING_HOURS },
            comment: place.comment || '',
            primaryTag: place.primaryTag || '',
            famousTags: Array.isArray(place.famousTags) ? place.famousTags : (place.famousTag ? [place.famousTag] : []),
            ledgerMinLe: ledger?.ledgerMinLe != null ? ledger.ledgerMinLe : '',
            ledgerBalanceLe: ledger?.ledgerBalanceLe != null ? ledger.ledgerBalanceLe : null,
            commissionPercent: ledger?.commissionPercent != null ? ledger.commissionPercent : '',
            cashbackPercent: ledger?.cashbackPercent != null ? ledger.cashbackPercent : '',
            pointsCapPercent: ledger?.pointsCapPercent != null ? ledger.pointsCapPercent : '',
            marketingFeeLe: ledger?.marketingFeeLe != null ? ledger.marketingFeeLe : '',
            marketingFeeMinBillLe: ledger?.marketingFeeMinBillLe != null ? ledger.marketingFeeMinBillLe : '',
            marketingFeeMaxLe: ledger?.marketingFeeMaxLe != null ? ledger.marketingFeeMaxLe : '',
            offers: Array.isArray(place.offers) ? place.offers.map((o) => ({
                id: o.id,
                name: o.name || '',
                description: o.description || '',
                commissionPercent: o.commissionPercent != null ? o.commissionPercent : '',
                cashbackPercent: o.cashbackPercent != null ? o.cashbackPercent : '',
                code: o.code || '',
                maxRedemptions: o.maxRedemptions != null ? o.maxRedemptions : '',
                startDate: toDateKey(o.startDate),
                expiryDate: toDateKey(o.expiryDate),
            })) : [],
        });
        setUploadedImages(placeImageUrlsForForm(place));
        setMainImageIndex(place.mainImageIndex || 0);
        setMenuImages(Array.isArray(place.menuImages) ? place.menuImages.map((u) => getImageDisplayUrl(typeof u === 'string' ? u : (u?.url || ''))).filter(Boolean) : []);
        setPlaceDialog(true);
    }, [token, fetchCategories]);

    /** Admin credit: top up the place wallet (cashback balance) for the open place. */
    const handleAddLedgerBalance = useCallback(async (amount) => {
        const value = Number(amount);
        if (!Number.isFinite(value) || value <= 0) {
            setError('Enter an amount greater than 0');
            return null;
        }
        try {
            const data = await addPlaceLedgerBalanceService(getHeaders(token), placeForm._id, value);
            const newBalance = Number(data?.ledgerBalanceLe ?? 0);
            setPlaceForm((prev) => ({
                ...prev,
                ledgerBalanceLe: Number.isFinite(data?.ledgerBalanceLe) ? data.ledgerBalanceLe : prev.ledgerBalanceLe,
                ledgerMinLe: data?.ledgerMinLe ?? prev.ledgerMinLe,
            }));
            setError('');
            setSuccess(`Added ${value.toLocaleString()} LE — new balance ${newBalance.toLocaleString()} LE`);
            return data;
        } catch (e) {
            setError(e.message || 'Failed to add balance');
            return null;
        }
    }, [token, placeForm._id]);

    const handleDeletePlace = useCallback(async (place) => {
        if (!window.confirm(`Are you sure you want to delete "${place.name}"?`)) return;
        try {
            const res = await apiFetch(apiUrl(`/places/${place._id}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('Place deleted successfully!'); loadDashboardData(); }
            else setError(data.error?.message || 'Failed to delete place');
        } catch (e) { setError('Failed to delete place: ' + e.message); }
    }, [token, loadDashboardData]);

    const handleAddTopicFeatured = useCallback(async (topicKey, body) => {
        try {
            const res = await apiFetch(apiUrl(`/topics/${topicKey}/featured`), { method: 'POST', headers: getHeaders(token), body: JSON.stringify(body) });
            const data = await res.json();
            if (res.ok) { setSuccess('Featured place added'); await fetchTopics(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to add featured place');
        } catch (e) { setError('Failed to add featured place: ' + e.message); }
    }, [token, fetchTopics]);

    const normalizePlaceId = (id) => {
        if (typeof id === 'string') return id;
        if (id && typeof id === 'object' && id.$oid) return id.$oid;
        if (id && typeof id === 'object' && id.oid) return id.oid;
        if (id && typeof id?.toString === 'function') return id.toString();
        return String(id);
    };

    const handleUpdateTopicFeatured = useCallback(async (topicKey, placeId, body) => {
        try {
            const placeIdStr = normalizePlaceId(placeId);
            const res = await apiFetch(apiUrl(`/topics/${topicKey}/featured/${placeIdStr}`), { method: 'PATCH', headers: getHeaders(token), body: JSON.stringify(body) });
            const data = await res.json();
            if (res.ok) { setSuccess('Featured updated'); await fetchTopics(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to update');
        } catch (e) { setError('Failed to update: ' + e.message); }
    }, [token, fetchTopics]);

    const handleRemoveTopicFeatured = useCallback(async (topicKey, placeId) => {
        if (!window.confirm('Remove this place from featured?')) return;
        try {
            const placeIdStr = normalizePlaceId(placeId);
            const res = await apiFetch(apiUrl(`/topics/${topicKey}/featured/${placeIdStr}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('Removed from featured'); await fetchTopics(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to remove');
        } catch (e) { setError('Failed to remove: ' + e.message); }
    }, [token, fetchTopics]);

    const handleCreateCategory = useCallback(async () => {
        try {
            const res = await apiFetch(apiUrl('/categories'), { method: 'POST', headers: getHeaders(token), body: JSON.stringify(categoryForm) });
            const data = await res.json();
            if (res.ok) {
                setSuccess('Category created successfully!');
                setCategoryDialog(false);
                setCategoryForm({ ...INITIAL_CATEGORY_FORM });
                loadDashboardData();
            } else setError(data.error?.message || 'Failed to create category');
        } catch (e) { setError('Failed to create category: ' + e.message); }
    }, [token, categoryForm, loadDashboardData]);

    const handleEditCategory = useCallback((category) => {
        setCategoryForm({ _id: category._id, name: category.name || '', icon: category.icon || '', description: category.description || '', color: category.color || '#2196F3', sortOrder: category.sortOrder || 0 });
        setCategoryDialog(true);
    }, []);

    const handleUpdateCategory = useCallback(async () => {
        try {
            const res = await apiFetch(apiUrl(`/categories/${categoryForm._id}`), { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(categoryForm) });
            const data = await res.json();
            if (res.ok) {
                setSuccess('Category updated successfully!');
                setCategoryDialog(false);
                setCategoryForm({ ...INITIAL_CATEGORY_FORM });
                loadDashboardData();
            } else setError(data.error?.message || 'Failed to update category');
        } catch (e) { setError('Failed to update category: ' + e.message); }
    }, [token, categoryForm, loadDashboardData]);

    const handleDeleteCategory = useCallback(async (category) => {
        if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) return;
        try {
            const res = await apiFetch(apiUrl(`/categories/${category._id}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('Category deleted successfully!'); loadDashboardData(); }
            else setError(data.error?.message || 'Failed to delete category');
        } catch (e) { setError('Failed to delete category: ' + e.message); }
    }, [token, loadDashboardData]);

    const handleCreateTag = useCallback(async () => {
        try {
            setError('');
            const res = await apiFetch(apiUrl('/tags'), { method: 'POST', headers: getHeaders(token), body: JSON.stringify(tagForm) });
            const data = await res.json();
            if (res.ok) { setSuccess('Tag created successfully!'); setTagDialog(false); await fetchTags(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to create tag');
        } catch (e) { setError('Failed to create tag: ' + e.message); }
    }, [token, tagForm, fetchTags]);

    const handleEditTag = useCallback((tag) => {
        setTagForm({ _id: tag._id, name: tag.name || '', slug: tag.slug || '', description: tag.description || '', sortOrder: tag.sortOrder ?? 0, tagType: tag.tagType || 'vibe' });
        setTagDialog(true);
    }, []);

    const handleSaveTag = useCallback(async () => {
        if (!tagForm._id) { await handleCreateTag(); return; }
        try {
            setError('');
            const res = await apiFetch(apiUrl(`/tags/${tagForm._id}`), { method: 'PUT', headers: getHeaders(token), body: JSON.stringify(tagForm) });
            const data = await res.json();
            if (res.ok) { setSuccess('Tag updated successfully!'); setTagDialog(false); await fetchTags(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to update tag');
        } catch (e) { setError('Failed to update tag: ' + e.message); }
    }, [token, tagForm, handleCreateTag, fetchTags]);

    const handleDeleteTag = useCallback(async (tag) => {
        if (!window.confirm(`Are you sure you want to delete "${tag.name}"?`)) return;
        try {
            const res = await apiFetch(apiUrl(`/tags/${tag._id}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('Tag deleted successfully!'); await fetchTags(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to delete tag');
        } catch (e) { setError('Failed to delete tag: ' + e.message); }
    }, [token, fetchTags]);

    const handleEditBanner = useCallback(async (banner) => {
        await Promise.all([fetchCategories(getHeaders(token)), fetchPlaces(getHeaders(token))]);
        const rawTargetId = banner.targetId;
        const targetId = (rawTargetId && typeof rawTargetId === 'object' && (rawTargetId._id || rawTargetId.id))
            ? (rawTargetId._id || rawTargetId.id)
            : (rawTargetId ?? '');
        setBannerForm({
            title: banner.title || '',
            description: banner.description || '',
            imageUrl: banner.imageUrl || '',
            featureType: banner.featureType || banner.targetType || 'place',
            targetType: banner.targetType || banner.featureType || 'place',
            targetId,
            order: banner.order || 0,
            startDate: toDateKey(banner.startDate) || todayDateKey(),
            endDate: toDateKey(banner.endDate),
            isActive: banner.isActive !== undefined ? banner.isActive : true,
            isFeaturedPlace: banner.isFeaturedPlace || false,
            _id: banner._id,
        });
        setBannerDialog(true);
    }, [token, fetchCategories, fetchPlaces]);

    const handleCreateFeaturedItem = useCallback(async () => {
        try {
            const isEditing = featuredItemForm._id;
            let startDateISO = toIsoUtcMidnight(featuredItemForm.startDate) || new Date().toISOString();
            let endDateISO = null;
            if (featuredItemForm.endDate?.trim()) {
                endDateISO = toIsoUtcMidnight(featuredItemForm.endDate);
            }
            const payload = {
                title: featuredItemForm.title.trim(),
                description: featuredItemForm.description?.trim() || '',
                imageUrl: featuredItemForm.imageUrl.trim(),
                targetType: featuredItemForm.targetType,
                targetId: featuredItemForm.targetId,
                isActive: featuredItemForm.isActive !== false,
                startDate: startDateISO,
                ...(endDateISO && { endDate: endDateISO }),
            };
            const url = isEditing ? apiUrl(`/featured-items/${featuredItemForm._id}`) : apiUrl('/featured-items');
            const res = await apiFetch(url, { method: isEditing ? 'PUT' : 'POST', headers: getHeaders(token), body: JSON.stringify(payload) });
            const data = await res.json();
            if (res.ok) {
                setSuccess(`Featured item ${isEditing ? 'updated' : 'created'} successfully!`);
                setFeaturedItemDialog(false);
                setFeaturedItemForm({ ...INITIAL_FEATURED_ITEM_FORM });
                await fetchFeaturedItems(getHeaders(token));
            } else {
                let errMsg = data.error?.message || `Failed to ${isEditing ? 'update' : 'create'} featured item`;
                if (data.error?.details && Array.isArray(data.error.details)) errMsg = data.error.details.map(e => (typeof e === 'object' ? Object.entries(e).map(([k, v]) => `${k}: ${v}`).join(', ') : String(e))).join('; ');
                setError(errMsg);
            }
        } catch (e) { setError(`Failed to ${featuredItemForm._id ? 'update' : 'create'} featured item: ` + e.message); }
    }, [token, featuredItemForm, fetchFeaturedItems]);

    const handleDeleteFeaturedItem = useCallback(async (itemId) => {
        if (!window.confirm('Are you sure you want to delete this featured item?')) return;
        try {
            const res = await apiFetch(apiUrl(`/featured-items/${itemId}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('Featured item deleted successfully!'); await fetchFeaturedItems(getHeaders(token)); }
            else setError(data.error?.message || 'Failed to delete featured item');
        } catch (e) { setError('Failed to delete featured item: ' + e.message); }
    }, [token, fetchFeaturedItems]);

    const handleDeleteBanner = useCallback(async (banner) => {
        if (!window.confirm(`Are you sure you want to delete "${banner.title}"?`)) return;
        try {
            const res = await apiFetch(apiUrl(`/features/${banner._id}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('Banner deleted successfully!'); loadDashboardData(); }
            else setError(data.error?.message || 'Failed to delete banner');
        } catch (e) { setError('Failed to delete banner: ' + e.message); }
    }, [token, loadDashboardData]);

    const handleDeleteUser = useCallback(async (user) => {
        if (user.role === 'admin') { setError('Cannot delete admin users'); return; }
        if (!window.confirm(`Are you sure you want to delete user "${user.name}" (${user.email})? This action cannot be undone.`)) return;
        try {
            const res = await apiFetch(apiUrl(`/users/${user._id}`), { method: 'DELETE', headers: getHeaders(token) });
            const data = await res.json();
            if (res.ok) { setSuccess('User deleted successfully!'); loadDashboardData(); }
            else setError(data.error?.message || 'Failed to delete user');
        } catch (e) { setError('Failed to delete user: ' + e.message); }
    }, [token, loadDashboardData]);

    const handleApplyRedemptionFilters = useCallback(async (nextFilters) => {
        const headers = getHeaders(token);
        const merged = { ...redemptionFilters, ...nextFilters, offset: 0 };
        const pid = String(merged.placeId || '').trim();
        const uid = String(merged.userId || '').trim();
        const oidRe = /^[a-fA-F0-9]{24}$/;
        if (pid && !oidRe.test(pid)) {
            setError('Place ID must be exactly 24 hexadecimal characters (open Places → View place and copy _id), or clear the field and use Search for place name instead.');
            return;
        }
        if (uid && !oidRe.test(uid)) {
            setError('User ID must be exactly 24 hexadecimal characters (from Users tab), or clear the field.');
            return;
        }
        setError('');
        setRedemptionFilters(merged);
        try {
            const data = await fetchRedemptionsService(headers, merged);
            if (data != null) {
                setRedemptions(data.items || []);
                setRedemptionsTotal(data.total ?? 0);
            } else {
                setRedemptions([]);
                setRedemptionsTotal(0);
                setError('Could not load redemptions (server error). Check that you are logged in as admin.');
            }
        } catch (e) {
            setRedemptions([]);
            setRedemptionsTotal(0);
            setError('Failed to load redemptions: ' + e.message);
        }
    }, [token, redemptionFilters]);

    const handleDeleteRedemption = useCallback(async (redemptionId, reason) => {
        try {
            const data = await deleteRedemptionService(getHeaders(token), redemptionId, reason);
            if (data.success) {
                setSuccess('Redemption deleted and audited');
                await Promise.all([
                    fetchRedemptions(getHeaders(token), redemptionFilters),
                    fetchRedemptionDeletionHistory(getHeaders(token)),
                    fetchAnalytics(getHeaders(token)),
                ]);
            } else {
                setError(data.error?.message || 'Failed to delete redemption');
            }
        } catch (e) {
            setError('Failed to delete redemption: ' + e.message);
        }
    }, [token, redemptionFilters, fetchRedemptions, fetchRedemptionDeletionHistory, fetchAnalytics]);

    const handleLoadPlaceRedemptionStats = useCallback(async (placeId) => {
        const id = String(placeId || '').trim();
        if (!id) return;
        const oidRe = /^[a-fA-F0-9]{24}$/;
        if (!oidRe.test(id)) {
            setSelectedPlaceRedemptionStats(null);
            setError('Load place stats: paste a valid 24-character Place ID from the Places tab (View place / edit).');
            return;
        }
        setError('');
        try {
            const data = await fetchPlaceRedemptionStatsService(getHeaders(token), id);
            if (data) {
                setSelectedPlaceRedemptionStats(data);
                setSuccess(`Stats loaded for ${data.placeName || 'place'}`);
            } else {
                setSelectedPlaceRedemptionStats(null);
                setError('Place not found or you do not have permission to view its stats.');
            }
        } catch (e) {
            setSelectedPlaceRedemptionStats(null);
            setError('Failed to load place stats: ' + e.message);
        }
    }, [token]);

    const handleApplyAmbassadorFilters = useCallback(async (nextFilters) => {
        const headers = getHeaders(token);
        const merged = { ...ambassadorFilters, ...nextFilters };
        setAmbassadorFilters(merged);
        await fetchAmbassadors(headers, merged);
    }, [token, ambassadorFilters, fetchAmbassadors]);

    const handleCreateAmbassador = useCallback(async (body) => {
        try {
            const data = await createAmbassadorService(getHeaders(token), body);
            if (data.success) {
                setSuccess('Ambassador created');
                await fetchAmbassadors(getHeaders(token), ambassadorFilters);
            } else {
                const details = data.error?.details;
                const detailMsg = Array.isArray(details) && details.length
                    ? details.join('; ')
                    : '';
                setError(detailMsg || data.error?.message || 'Failed to create ambassador');
            }
        } catch (e) { setError('Failed to create ambassador: ' + e.message); }
    }, [token, fetchAmbassadors, ambassadorFilters]);

    const handleUpdateAmbassador = useCallback(async (id, body) => {
        try {
            const data = await updateAmbassadorService(getHeaders(token), id, body);
            if (data.success) {
                setSuccess('Ambassador updated');
                await fetchAmbassadors(getHeaders(token), ambassadorFilters);
                return true;
            }
            setError(data.error?.message || 'Failed to update ambassador');
            return false;
        } catch (e) { setError('Failed to update ambassador: ' + e.message); }
        return false;
    }, [token, fetchAmbassadors, ambassadorFilters]);

    const handleDeleteAmbassador = useCallback(async (id, reason = 'Removed by admin from dashboard') => {
        if (!window.confirm('Delete this ambassador?')) return;
        try {
            const data = await deleteAmbassadorWithReasonService(getHeaders(token), id, reason);
            if (data.success) {
                setSuccess('Ambassador deleted');
                await Promise.all([
                    fetchAmbassadors(getHeaders(token), ambassadorFilters),
                    fetchAmbassadorDeletionHistory(getHeaders(token)),
                ]);
            } else {
                setError(data.error?.message || 'Failed to delete ambassador');
            }
        } catch (e) { setError('Failed to delete ambassador: ' + e.message); }
    }, [token, fetchAmbassadors, ambassadorFilters, fetchAmbassadorDeletionHistory]);

    const handleViewAmbassadorRedemptions = useCallback(async (ambassadorOrId, filters = {}) => {
        const id = typeof ambassadorOrId === 'string' || typeof ambassadorOrId === 'number'
            ? ambassadorOrId
            : (ambassadorOrId?._id || ambassadorOrId?.id);
        const code = typeof ambassadorOrId === 'object' && ambassadorOrId != null
            ? ambassadorOrId.code
            : null;
        try {
            const headers = getHeaders(token);
            const [data, auditRows] = await Promise.all([
                fetchAmbassadorRedemptionsService(headers, id, filters),
                code
                    ? fetchRedemptionDeletionHistoryService(headers, { ambassadorCode: code })
                    : Promise.resolve([]),
            ]);
            setAmbassadorRedemptions(data || []);
            setAmbassadorRedemptionDeletionAudit(auditRows || []);
        } catch (e) {
            setAmbassadorRedemptions([]);
            setAmbassadorRedemptionDeletionAudit([]);
            setError('Failed to load ambassador redemptions: ' + e.message);
        }
    }, [token]);

    return {
        tabValue,
        setTabValue,
        loading,
        error,
        success,
        setError,
        setSuccess,
        stats,
        places,
        categories,
        tags,
        banners,
        featuredItems,
        users,
        businessSubmissions,
        analytics,
        selectedBusinessSubmission,
        setSelectedBusinessSubmission,
        selectedPlace,
        setSelectedPlace,
        businessSubmissionDetailsOpen,
        setBusinessSubmissionDetailsOpen,
        uploadedImages,
        setUploadedImages,
        mainImageIndex,
        setMainImageIndex,
        uploadingImages,
        menuImages,
        setMenuImages,
        uploadingMenuImages,
        handleMenuImageUpload,
        handleRemoveMenuImage,
        uploadingBannerImage,
        uploadingFeaturedItemImage,
        placeDialog,
        setPlaceDialog,
        placeDetailsOpen,
        setPlaceDetailsOpen,
        bannerDialog,
        setBannerDialog,
        featuredItemDialog,
        setFeaturedItemDialog,
        categoryDialog,
        setCategoryDialog,
        placeForm,
        setPlaceForm,
        categoryForm,
        setCategoryForm,
        tagDialog,
        setTagDialog,
        tagForm,
        setTagForm,
        bannerForm,
        setBannerForm,
        featuredItemForm,
        setFeaturedItemForm,
        topics,
        redemptions,
        redemptionsTotal,
        redemptionDeletionHistory,
        redemptionFilters,
        setRedemptionFilters,
        selectedPlaceRedemptionStats,
        ambassadors,
        ambassadorRedemptions,
        ambassadorRedemptionDeletionAudit,
ambassadorDeletionHistory,
        ambassadorFilters,
        setAmbassadorFilters,
        reports,
        reportsSummary,
        reportFilters,
        setReportFilters,
        fetchReports,
        handleApplyReportFilters,
        handleUpdateReportStatus,
        fetchTopics,
        fetchRedemptions,
        fetchRedemptionDeletionHistory,
        fetchAmbassadors,
        fetchAmbassadorDeletionHistory,
        loadDashboardData,
        handleAddTopicFeatured,
        handleUpdateTopicFeatured,
        handleRemoveTopicFeatured,
        fetchPlaces,
        fetchCategories,
        fetchTags,
        getHeaders,
        handleUpdateBusinessStatus,
        handleImageUpload,
        handleRemoveImage,
        handleBannerImageUpload,
        handleFeaturedItemImageUpload,
        handleSetMainImage,
        handleCreatePlace,
        handleCreateBanner,
        handleViewPlace,
        handleEditPlace,
        handleAddLedgerBalance,
        handleDeletePlace,
        handleCreateCategory,
        handleEditCategory,
        handleUpdateCategory,
        handleDeleteCategory,
        handleCreateTag,
        handleEditTag,
        handleSaveTag,
        handleDeleteTag,
        handleEditBanner,
        handleCreateFeaturedItem,
        handleDeleteFeaturedItem,
        handleDeleteBanner,
        handleDeleteUser,
        handleApplyRedemptionFilters,
        handleDeleteRedemption,
        handleLoadPlaceRedemptionStats,
        handleApplyAmbassadorFilters,
        handleCreateAmbassador,
        handleUpdateAmbassador,
        handleDeleteAmbassador,
        handleViewAmbassadorRedemptions,
    };
}
